import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ProviderReferralService } from '../../../../../js/api/services/ProviderReferralService';
import { useCanShowReferralButton } from '../../../../../js/app/hooks/providers';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { Heading } from '../../../components/heading';
import { ProviderReferralContent } from '../../../components/provider-referral-content';
import { ProviderReferralDialog } from '../../../components/provider-referral-dialog';
import { Text } from '../../../components/text';
import { PROVIDER_REFERRALS_LIST_QUERY_KEY } from '../../../constants/queryKeys';
import { MAIN_CONTENT_AREA, PAGE_HEADER } from '../../../utils/layoutStyles';
import { ReferralCard } from './ReferralCard';
import { ReferralsSummaryCards } from './ReferralsSummaryCards';

const PAGE_SIZE = 50;

export function ProviderReferralsWrapper() {
  useDocumentTitle('Referrals');
  const canShowReferralButton = useCanShowReferralButton();
  const [isReferralDialogOpen, setIsReferralDialogOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: [PROVIDER_REFERRALS_LIST_QUERY_KEY, 0, PAGE_SIZE],
    queryFn: () => ProviderReferralService.providerReferralList({ limit: PAGE_SIZE, offset: 0 }),
  });

  const referrals = data?.results ?? [];

  return (
    <>
      <main className={MAIN_CONTENT_AREA}>
        <div className="tw:flex tw:flex-col tw:gap-6">
          {/* Referral list */}
          {error ? (
            <div className="tw:rounded-lg tw:border tw:border-zinc-200 tw:bg-zinc-50 tw:p-6 tw:text-center tw:text-zinc-600">
              Unable to load referrals. Please try again later.
            </div>
          ) : isLoading ? (
            <div className="tw:flex tw:items-center tw:justify-center tw:py-12">
              <div className="tw:h-8 tw:w-8 tw:animate-spin tw:rounded-full tw:border-2 tw:border-zinc-300 tw:border-t-zinc-600" />
            </div>
          ) : referrals.length === 0 ? (
            <div className="tw:flex tw:flex-col tw:w-4xl tw:mx-auto">
              <div>
                <Heading level={2} className="tw:!text-brand-green-0 tw:!mb-0">
                  Locums Without the Agency. Finally.
                </Heading>
                <Text className="tw:mt-2 tw:text-sm tw:text-zinc-600">
                  Help your colleagues keep more of what they earn.
                </Text>
              </div>
              <ProviderReferralContent active={true} />
            </div>
          ) : (
            <>
              {/* Header */}
              <div className={PAGE_HEADER}>
                <div className="tw:flex tw:flex-col tw:gap-1">
                  <Heading level={1} className="tw:!text-brand-green-0">
                    <i className="far fa-fw fa-dollar-sign tw:mr-1" />
                    Referrals
                  </Heading>
                  <p className="tw:!mb-0 tw:text-sm tw:text-zinc-600">
                    Help your colleagues keep more of what they earn.
                  </p>
                </div>
              </div>

              {/* Summary cards */}
              <ReferralsSummaryCards referrals={referrals} isLoading={isLoading} />

              <div className="tw:@container">
                <div className="tw:grid tw:gap-4 tw:grid-cols-1 tw:@min-[732px]:grid-cols-2 tw:@min-[1024px]:grid-cols-3">
                  {referrals.map((referral) => (
                    <ReferralCard key={referral.id} referral={referral} />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {canShowReferralButton && (
          <ProviderReferralDialog
            open={isReferralDialogOpen}
            onClose={() => setIsReferralDialogOpen(false)}
          />
        )}
      </main>
    </>
  );
}
